import {
  Button,
  Flex,
  Form,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Stack,
  Text
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { emailRegex } from '../../../../constants/regexes'
import { formatCustomerProfileType } from '../../../../helpers/formatCustomerProfileType'

export const MSG = defineMessages({
  add: {
    id: 'AddPreferredAgentForm.add',
    defaultMessage: 'Yes, add'
  },
  email: {
    id: 'AddPreferredAgentForm.email',
    defaultMessage: 'Email'
  },
  emailInvalid: {
    id: 'AddPreferredAgentForm.emailInvalid',
    defaultMessage: 'Invalid email address'
  },
  emailRequired: {
    id: 'AddPreferredAgentForm.emailRequired',
    defaultMessage: 'Email is required'
  },
  error: {
    id: 'AddPreferredAgentForm.error',
    defaultMessage: 'Failed to add partner'
  },
  firstName: {
    id: 'AddPreferredAgentForm.firstName',
    defaultMessage: 'First name'
  },
  firstNameRequired: {
    id: 'AddPreferredAgentForm.firstNameRequired',
    defaultMessage: 'First name is required'
  },
  heading: {
    id: 'VerificationFlowModal.addPartner',
    defaultMessage: 'Do you have a different {customerType} you would like to add?'
  },
  lastName: {
    id: 'AddPreferredAgentForm.lastName',
    defaultMessage: 'Last name'
  },
  lastNameRequired: {
    id: 'AddPreferredAgentForm.lastNameRequired',
    defaultMessage: 'Last name is required'
  },
  notAtThisTime: {
    id: 'AddPreferredAgentForm.notAtThisTime',
    defaultMessage: 'Not at this time'
  }
})

interface AddPreferredAgentFormProps {
  isReplaceAction?: boolean
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (data: FormData) => Promise<void>
  customerType?: 'RealEstateAgent' | 'LoanOfficer' | 'InsuranceAgent' | 'TitleAgent'
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  type: 'RealEstateAgent' | 'LoanOfficer' | 'InsuranceAgent' | 'TitleAgent'
}

export const AddPreferredAgentForm = ({
  isReplaceAction,
  isSubmitting,
  onClose,
  onSubmit,
  customerType = 'RealEstateAgent'
}: AddPreferredAgentFormProps) => {
  const intl = useIntl()
  const formattedCustomerType = formatCustomerProfileType(customerType).toLowerCase()

  return (
    <Form onSubmit={onSubmit}>
      {({ formState: { errors } }) => (
        <Stack gap={4}>
          <ModalHeader>
            <Text size='lg' fontWeight='semibold' color='neutral.900'>
              {intl.formatMessage(MSG.heading, { customerType: formattedCustomerType })}
            </Text>
          </ModalHeader>

          <ModalBody pb={6}>
            <Flex direction='column' gap={4}>
              <FormControl isRequired isInvalid={!!errors.firstName}>
                <FormLabel>{intl.formatMessage(MSG.firstName)}</FormLabel>
                <Input
                  name='firstName'
                  placeholder={intl.formatMessage(MSG.firstName)}
                  required
                  tracking={{
                    guid: 'dmRqY3WoqEqFyYSHVg84ZP',
                    ui_context: 'Add preferred agent form first name input',
                    descriptor: 'Add preferred agent form first name input'
                  }}
                  rules={{ required: `${intl.formatMessage(MSG.firstNameRequired)}` }}
                />
                {errors.firstName ? (
                  <>
                    <FormErrorMessage>{errors.firstName.message?.toString()}</FormErrorMessage>
                  </>
                ) : (
                  <>
                    <FormHelperText>{'\u200B'}</FormHelperText>
                  </>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.lastName}>
                <FormLabel>{intl.formatMessage(MSG.lastName)}</FormLabel>
                <Input
                  name='lastName'
                  placeholder={intl.formatMessage(MSG.lastName)}
                  required
                  tracking={{
                    guid: '4kz6vbdgZgsYg3BUhWwacE',
                    ui_context: 'Add preferred agent form last name input',
                    descriptor: 'Add preferred agent form last name input'
                  }}
                  rules={{ required: `${intl.formatMessage(MSG.lastNameRequired)}` }}
                />
                {errors.lastName ? (
                  <>
                    <FormErrorMessage>{errors.lastName.message?.toString()}</FormErrorMessage>
                  </>
                ) : (
                  <>
                    <FormHelperText>{'\u200B'}</FormHelperText>
                  </>
                )}
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.email}>
                <FormLabel>{intl.formatMessage(MSG.email)}</FormLabel>
                <Input
                  name='email'
                  type='email'
                  placeholder={intl.formatMessage(MSG.email)}
                  required
                  tracking={{
                    guid: 'tB1ameFz76rcMmywPXJLiS',
                    ui_context: 'Add preferred agent form email input',
                    descriptor: 'Add preferred agent form email input'
                  }}
                  rules={{
                    // TODO: Share these w/ tune value form translations
                    required: intl.formatMessage(MSG.emailRequired),
                    pattern: {
                      value: emailRegex,
                      message: intl.formatMessage(MSG.emailInvalid)
                    }
                  }}
                />
                {errors.email ? (
                  <>
                    <FormErrorMessage>{errors.email.message?.toString()}</FormErrorMessage>
                  </>
                ) : (
                  <>
                    <FormHelperText>{'\u200B'}</FormHelperText>
                  </>
                )}
              </FormControl>
              <Input type='hidden' name='type' value={customerType} />
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Stack direction='row' gap={2} justifyContent='flex-end'>
              <Button
                variant='solid'
                colorScheme='secondary'
                onClick={() => {
                  onClose()
                }}
                tracking={{
                  guid: 'omoQkyaEqhKrX6Gwx6b7yL',
                  ui_context: 'Add preferred agent form cancel',
                  descriptor: 'Cancel add preferred agent form'
                }}
              >
                {intl.formatMessage(MSG.notAtThisTime)}
              </Button>
              <Button
                mr={3}
                colorScheme='primary'
                type='submit'
                isLoading={isSubmitting}
                tracking={{
                  guid: '5UEdaEeK4tsp2twTigB4Zu',
                  ui_context: 'Add preferred agent form submit',
                  descriptor: 'Add preferred agent form submit'
                }}
              >
                {intl.formatMessage(MSG.add)}
              </Button>
            </Stack>
          </ModalFooter>
        </Stack>
      )}
    </Form>
  )
}
